import { useCallback, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import useDebounce from 'hooks/useDebounce';
import useColumnPagination from 'hooks/useColumnPagination';
import useFiltersState from 'state/useFiltersState';
import TrelloColumn from '../TrelloColumn/TrelloColumn';
import { COLUMN_TYPE } from '../TrelloColumn/TrelloColumnTypes';
import { TbordOneToneIcon } from 'components/NudosComponents/BordOneToneIcon/BordOneToneIcon.types';
import { IProductList, ORDER_SHIPMENT_STATUS, ORDER_STATUS } from 'types/orders';
import OperationsCard from 'views/TrackingOrder/components/OperationsCard/OperationsCard';
import useOrdersOperationsModule from './useOrdersOperationsModule';
import OrdersCancelledModal from '../OrdersCancelledModal/OrdersCancelledModal';
import ButtonCancelOrders from '../ButtonCancelOrders/ButtonCancelOrders';
import OrdersTrackingOperationsFilters from '../OrdersTrackingOperationsFilters/OrdersTrackingOperationsFilters';
import { getListOfOrdersFirstView } from 'services/orders';
import { optionsParamsSwr } from 'utils/optionsParamsSwr';
import './OrdersListingOperations.scss';

const DEBOUNCE_DURATION = 1500;

/**
 * Componente que muestra el listado de órdenes en formato de operaciones con columnas tipo Trello.
 * Gestiona la visualización de órdenes por etapa del proceso logístico.
 * Realiza llamadas al API de forma independiente usando SWR para caché y revalidación.
 */
const OrdersListingOperations = () => {
  const { ordersFilters, setOrdersFilters, ordersSearchbarText, propertyToFilterSearch } = useFiltersState();
  const { orderFilters } = useOrdersOperationsModule();

  const debouncedSearchText = useDebounce(ordersSearchbarText, DEBOUNCE_DURATION);

  const searchQueryParam = useMemo(() => {
    if (!debouncedSearchText || !propertyToFilterSearch?.propertyToFilter) return '';
    const searchObject = encodeURIComponent(
      JSON.stringify({
        name: debouncedSearchText.trim(),
        propertyToFilter: propertyToFilterSearch?.propertyToFilter
      })
    );
    return `search=${searchObject}`;
  }, [debouncedSearchText, propertyToFilterSearch?.propertyToFilter]);

  const filtersQueryParamString = useMemo(() => {
    return orderFilters?.length ? JSON.stringify(orderFilters) : '';
  }, [orderFilters]);

  const filtersQueryParam = useMemo(() => {
    return filtersQueryParamString ? `filters=${filtersQueryParamString}` : '';
  }, [filtersQueryParamString]);

  const debouncedFiltersQuery = useDebounce(filtersQueryParam, DEBOUNCE_DURATION);
  const prevQueryParamsRef = useRef<string>('');

  const stableQueryParams = useMemo(() => {
    const hasSearch = searchQueryParam && searchQueryParam.length > 0;
    const hasFilters = debouncedFiltersQuery && debouncedFiltersQuery.length > 0;

    if (!hasSearch && !hasFilters) {
      const result = '';
      if (prevQueryParamsRef.current === result) return prevQueryParamsRef.current;
      prevQueryParamsRef.current = result;
      return result;
    }

    const result =
      hasFilters && hasSearch
        ? `?${debouncedFiltersQuery}&${searchQueryParam}`
        : `?${hasFilters ? debouncedFiltersQuery : searchQueryParam}`;

    if (prevQueryParamsRef.current === result) return prevQueryParamsRef.current;
    prevQueryParamsRef.current = result;
    return result;
  }, [searchQueryParam, debouncedFiltersQuery]);

  const queryParams = stableQueryParams;

  const swrKeyOperations = useMemo(() => ['orders-operations', queryParams], [queryParams]);

  const { data: ordersData = [], isLoading } = useSWR(
    swrKeyOperations,
    () => getListOfOrdersFirstView(`/backoffice/trello${queryParams}`),
    optionsParamsSwr()
  );

  /**
   * Formatea las órdenes agregando un ID como string.
   * @param ordersList - Lista de órdenes a formatear
   * @returns Nueva lista de órdenes con ID formateado
   */
  const formatOrders = (ordersList: IProductList[]): IProductList[] => {
    return ordersList.map(order => ({
      ...order,
      id: JSON.stringify(order.orderId)
    }));
  };

  const ordersList = useMemo(() => formatOrders(ordersData || []), [ordersData]);

  /**
   * Maneja el click en un país para filtrar por ese destino.
   * @param countryName - Nombre del país a filtrar
   */
  const handleCountryClick = useCallback(
    (countryName: string) => {
      const countryFilterName = 'countryDestination';
      const currentFilters = [...ordersFilters];
      const countryFilterIndex = currentFilters.findIndex(filter => filter.name === countryFilterName);

      if (countryFilterIndex >= 0) {
        const existingValues = currentFilters[countryFilterIndex].values;
        const countryAlreadySelected = existingValues.includes(countryName);

        if (countryAlreadySelected) return;

        currentFilters[countryFilterIndex] = {
          ...currentFilters[countryFilterIndex],
          values: [countryName]
        };
      } else {
        currentFilters.push({
          name: countryFilterName,
          includes: 'in',
          values: [countryName]
        });
      }

      setOrdersFilters(currentFilters);
    },
    [ordersFilters, setOrdersFilters]
  );

  const paginationConfirmed = useColumnPagination();
  const paginationPreparation = useColumnPagination();
  const paginationTransitWarehouse = useColumnPagination();
  const paginationInWarehouse = useColumnPagination();
  const paginationTransitDestination = useColumnPagination();
  const paginationDelivered = useColumnPagination();

  const [openCancelOrdersModal, setOpenCancelOrdersModal] = useState(false);

  const ordersCancelled = useMemo(() => {
    return ordersList.filter(order => order.statusName === ORDER_STATUS.CANCELLED);
  }, [ordersList]);

  const buttonTextCancelOrders = `Ver canceladas (${ordersCancelled?.length ?? 0})`;

  const filteredConfirmedOrders = useMemo(() => {
    return ordersList.filter(order => order.statusName === ORDER_STATUS.PENDING);
  }, [ordersList]);

  const filteredPreparationOrders = useMemo(() => {
    return ordersList.filter(order => order.statusName === ORDER_STATUS.ACTIVE);
  }, [ordersList]);

  const filteredTransitWarehouseOrders = useMemo(() => {
    return ordersList.filter(order => order.statusName === ORDER_SHIPMENT_STATUS.TRANSIT_TO_WAREHOUSE);
  }, [ordersList]);

  const filteredInWarehouseOrders = useMemo(() => {
    return ordersList.filter(order => order.statusName === ORDER_SHIPMENT_STATUS.IN_WAREHOUSE);
  }, [ordersList]);

  const filteredTransitDestinationOrders = useMemo(() => {
    return ordersList.filter(order => order.statusName === ORDER_SHIPMENT_STATUS.TRANSIT_TO_DESTINATION);
  }, [ordersList]);

  const filteredDeliveredOrders = useMemo(() => {
    return ordersList.filter(order => order.statusName === ORDER_STATUS.DELIVERED);
  }, [ordersList]);

  const listOrdersConfirmed = useMemo(
    () => filteredConfirmedOrders.slice(0, paginationConfirmed.visibleCount),
    [filteredConfirmedOrders, paginationConfirmed.visibleCount]
  );

  const listOrdersPreparation = useMemo(
    () => filteredPreparationOrders.slice(0, paginationPreparation.visibleCount),
    [filteredPreparationOrders, paginationPreparation.visibleCount]
  );

  const listOrdersTransitWarehouse = useMemo(
    () => filteredTransitWarehouseOrders.slice(0, paginationTransitWarehouse.visibleCount),
    [filteredTransitWarehouseOrders, paginationTransitWarehouse.visibleCount]
  );

  const listOrdersInWarehouse = useMemo(
    () => filteredInWarehouseOrders.slice(0, paginationInWarehouse.visibleCount),
    [filteredInWarehouseOrders, paginationInWarehouse.visibleCount]
  );

  const listOrdersTransitDestination = useMemo(
    () => filteredTransitDestinationOrders.slice(0, paginationTransitDestination.visibleCount),
    [filteredTransitDestinationOrders, paginationTransitDestination.visibleCount]
  );

  const listOrdersDelivered = useMemo(
    () => filteredDeliveredOrders.slice(0, paginationDelivered.visibleCount),
    [filteredDeliveredOrders, paginationDelivered.visibleCount]
  );

  /**
   * Alterna la visibilidad del modal de órdenes canceladas.
   */
  const handleOpenOrCloseCancelOrdersModal = () => setOpenCancelOrdersModal(!openCancelOrdersModal);

  const columns = [
    {
      title: 'Confirmado',
      subtitle: 'Productos listos para comenzar a gestionar',
      variantIcon: 'check' as TbordOneToneIcon,
      items: listOrdersConfirmed,
      isLoading: !!isLoading,
      isLoadingMoreItems: paginationConfirmed.isLoadingMore,
      hasMoreItems: paginationConfirmed.hasMoreItems(filteredConfirmedOrders.length),
      onLoadMore: paginationConfirmed.handleLoadMore,
      columnType: COLUMN_TYPE.CONFIRMED
    },
    {
      title: 'Preparación',
      subtitle: 'Preparando los productos del envío',
      variantIcon: 'box' as TbordOneToneIcon,
      items: listOrdersPreparation,
      isLoading: !!isLoading,
      isLoadingMoreItems: paginationPreparation.isLoadingMore,
      hasMoreItems: paginationPreparation.hasMoreItems(filteredPreparationOrders.length),
      onLoadMore: paginationPreparation.handleLoadMore,
      columnType: COLUMN_TYPE.PREPARATION
    },
    {
      title: 'Tránsito a bodega',
      subtitle: 'Pasa por bodega antes del destino final',
      variantIcon: 'flag' as TbordOneToneIcon,
      items: listOrdersTransitWarehouse,
      isLoading: !!isLoading,
      isLoadingMoreItems: paginationTransitWarehouse.isLoadingMore,
      hasMoreItems: paginationTransitWarehouse.hasMoreItems(filteredTransitWarehouseOrders.length),
      onLoadMore: paginationTransitWarehouse.handleLoadMore,
      columnType: COLUMN_TYPE.TRANSIT_TO_WAREHOUSE
    },
    {
      title: 'En bodega',
      subtitle: 'Los productos llegaron a bodega',
      variantIcon: 'warehouse' as TbordOneToneIcon,
      items: listOrdersInWarehouse,
      isLoading: !!isLoading,
      isLoadingMoreItems: paginationInWarehouse.isLoadingMore,
      hasMoreItems: paginationInWarehouse.hasMoreItems(filteredInWarehouseOrders.length),
      onLoadMore: paginationInWarehouse.handleLoadMore,
      columnType: COLUMN_TYPE.IN_WAREHOUSE
    },
    {
      title: 'Tránsito a destino final',
      subtitle: 'Se encuentra en proceso de entrega',
      variantIcon: 'truck' as TbordOneToneIcon,
      items: listOrdersTransitDestination,
      isLoading: !!isLoading,
      isLoadingMoreItems: paginationTransitDestination.isLoadingMore,
      hasMoreItems: paginationTransitDestination.hasMoreItems(filteredTransitDestinationOrders.length),
      onLoadMore: paginationTransitDestination.handleLoadMore,
      columnType: COLUMN_TYPE.TRANSIT_TO_DESTINATION
    },
    {
      title: 'Entregado',
      subtitle: 'Productos entregados con éxito',
      variantIcon: 'doubleCheck' as TbordOneToneIcon,
      items: listOrdersDelivered,
      isLoading: !!isLoading,
      isLoadingMoreItems: paginationDelivered.isLoadingMore,
      hasMoreItems: paginationDelivered.hasMoreItems(filteredDeliveredOrders.length),
      onLoadMore: paginationDelivered.handleLoadMore,
      columnType: COLUMN_TYPE.DELIVERED
    }
  ];

  return (
    <div id="ordersListingOperations">
      <div className="containerHeaderOperations">
        <OrdersTrackingOperationsFilters orderList={ordersList} />
        <ButtonCancelOrders
          handleClick={handleOpenOrCloseCancelOrdersModal}
          buttonText={buttonTextCancelOrders}
          isDisabled={ordersCancelled?.length === 0 || isLoading}
        />
      </div>
      <div className="columnsContainerOperations">
        {columns.map(column => (
          <TrelloColumn
            key={column.title}
            title={column.title}
            subtitle={column.subtitle}
            variantIcon={column.variantIcon}
            items={column?.items}
            isLoading={column?.isLoading}
            columnType={column.columnType}
            renderItem={item => <OperationsCard key={item?.orderId} data={item} onCountryClick={handleCountryClick} />}
            onLoadMore={column?.onLoadMore}
            hasMoreItems={column?.hasMoreItems}
            isLoadingMoreItems={column?.isLoadingMoreItems}
            trelloColumnHeaderCustomClassName="trelloColumnHeaderOperations"
          />
        ))}
      </div>
      {openCancelOrdersModal && (
        <OrdersCancelledModal isOpen={openCancelOrdersModal} onClose={handleOpenOrCloseCancelOrdersModal} />
      )}
    </div>
  );
};

export default OrdersListingOperations;
